import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from 'redux/hooks'
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {Dialog} from "primereact/dialog";
import {ConfirmDialog} from "primereact/confirmdialog";
import AudioControlDialogInfo from "../AudioControlDialogInfo";
import AudioControlDialogManagement from "../DialogManagement";
import AudioControlDialogGeneralSettings from "../AudioControlDialogGeneralSettings";
import AudioControlDialogDetails from "../AudioControlDialogDetails";
import {getAudioControlPermissions} from "../../../../redux/permissions/permissionsUtils";
import {setAudioControlDialogVisible, setAudioControlErrorField} from "../../../../redux/actions/actionsAudioControl";
import {
    archiveOrUnArchiveAudioControlById,
    createAudioControl,
    updateAudioControl,
} from "../../../../redux/api/apiAudioControl";
import {validateAudioControlData} from "../../../../redux/validator/validator";
import type {AudioControlErrorFields} from "../audioControlQuestionnaire";

const TAB_GENERAL = 0;
const TAB_QUESTIONNAIRE = 1;

const AudioControlDialog = (props: { setSelectedRowData?: (value: number | null) => void }) => {
    const dispatch = useAppDispatch();
    const audioControlData = useAppSelector(state => state.AudioControlListReducer.audioControlData);
    const dialogVisible = useAppSelector(state => state.AudioControlListReducer.audioControlDialogVisible);
    const archiveTableToggle = useAppSelector(state => state.AudioControlListReducer.archiveAudioControlTableToggle);
    const saveLoader = useAppSelector(state => state.AudioControlListReducer.saveLoader);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showDeleteNotification, setShowDeleteNotification] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const errorFields = useAppSelector(state => state.AudioControlListReducer.errorFields);
    const audioControlPermissions = getAudioControlPermissions(rolesVision);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);

    const {
        create: hasCreatePermission,
        update: hasUpdatePermission,
        delete: hasDeletePermission,
    } = audioControlPermissions;

    useEffect(() => {
        setActiveIndex(TAB_GENERAL);
    }, [dialogVisible]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const focusTabByValidationErrors = (validationErrors: AudioControlErrorFields) => {
        if (!validationErrors) {
            return;
        }
        if (validationErrors.name || validationErrors.nameUniq || validationErrors.branchId) {
            setActiveIndex(TAB_GENERAL);
            return;
        }
        if (validationErrors.blocks || validationErrors.questionnaire) {
            setActiveIndex(TAB_QUESTIONNAIRE);
        }
    };

    useEffect(() => {
        focusTabByValidationErrors(errorFields);
    }, [errorFields]);

    const closeDialogVisible = () => {
        dispatch(setAudioControlDialogVisible(false));
        dispatch(setAudioControlErrorField(null));
    };

    const showSaveChangesNotification = () => {
        closeDialogVisible();
    };

    const handleUpdate = () => {
        if (!jwtToken?.jwtToken) {
            return;
        }

        const validationErrors = validateAudioControlData(audioControlData);
        if (!validationErrors) {
            dispatch(updateAudioControl(audioControlData, jwtToken.jwtToken));
        } else {
            dispatch(setAudioControlErrorField(validationErrors));
            focusTabByValidationErrors(validationErrors);
        }
    };

    const handleCreate = () => {
        if (!jwtToken?.jwtToken) {
            return;
        }

        const validationErrors = validateAudioControlData(audioControlData);
        if (!validationErrors) {
            dispatch(createAudioControl(audioControlData, jwtToken.jwtToken));
        } else {
            dispatch(setAudioControlErrorField(validationErrors));
            focusTabByValidationErrors(validationErrors);
        }
    };

    const deleteItem = () => {
        if (!hasDeletePermission) {
            return;
        }
        if (jwtToken?.jwtToken && audioControlData?.id) {
            dispatch(archiveOrUnArchiveAudioControlById(audioControlData.id, jwtToken.jwtToken));
        }
        closeDialogVisible();
        props.setSelectedRowData?.(null);
    };

    const footer = (
        <div className="dialog-footer">
            <Divider/>
            <div className="buttons">
                {archiveTableToggle && hasDeletePermission && audioControlData?.id && (
                    <Button
                        icon={windowWidth > 1248 ? "pi pi-trash" : ''}
                        className="p-button-outlined"
                        label="Видалити"
                        disabled={saveLoader}
                        onClick={() => setShowDeleteNotification(true)}
                    />
                )}
                <Button
                    icon={windowWidth > 1248 ? "pi pi-ban" : ''}
                    className="p-button-outlined"
                    label="Відміна"
                    loading={saveLoader}
                    onClick={showSaveChangesNotification}
                />
                {hasCreatePermission && !audioControlData?.id && (
                    <Button
                        icon={windowWidth > 1248 ? "pi pi-save" : ''}
                        label="Створити та зберегти"
                        loading={saveLoader}
                        onClick={handleCreate}
                    />
                )}
                {hasUpdatePermission && audioControlData?.id && (
                    <Button
                        icon={windowWidth > 1248 ? "pi pi-save" : ''}
                        label="Зберегти"
                        loading={saveLoader}
                        onClick={handleUpdate}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            header="Налаштування аудіоконтролю"
            footer={archiveTableToggle ? footer : ''}
            visible={dialogVisible}
            dismissableMask={false}
            closeOnEscape={false}
            draggable={true}
            blockScroll={true}
            position="top"
            className='dialogInfo'
            style={{width: '95vw', maxWidth: "1400px", minHeight: "70vh", zIndex: '1101px !important'}}
            onHide={showSaveChangesNotification}
        >
            {audioControlData && <div className="company-info-popup">
                <ConfirmDialog
                    visible={showDeleteNotification}
                    onHide={() => setShowDeleteNotification(false)}
                    message="Анкету буде архівовано та деактивовано. Архівувати?"
                    icon="pi pi-exclamation-triangle"
                    acceptLabel="Продовжити"
                    rejectLabel="Відмінити"
                    accept={deleteItem}
                />
                <AudioControlDialogInfo/>
                <Divider style={{margin: '0 0 20px 0'}}/>
                {audioControlData?.id && (
                    <>
                        <AudioControlDialogManagement/>
                        <Divider style={{margin: '0 0 20px 0'}}/>
                    </>
                )}
                <TabView
                    className="user-dialog-info"
                    activeIndex={activeIndex}
                    onTabChange={(event) => setActiveIndex(event.index)}
                >
                    <TabPanel header="Загальні налаштування">
                        <AudioControlDialogGeneralSettings/>
                    </TabPanel>
                    <TabPanel header="Налаштування анкети">
                        <AudioControlDialogDetails/>
                    </TabPanel>
                </TabView>
            </div>}
        </Dialog>
    );
};

export default AudioControlDialog;
